/**
 * Backend API client
 * Handles all HTTP communication with the backend.
 * Automatically includes the JWT token in requests.
 *
 * Note: this is a plain class, not a hook — the token is supplied via a getter
 * function so callers (hooks/components) can wire it to auth state.
 */

import type { FinalDraftFee, FinalLineEdit, FinalLineChange } from '../lib/estimate';

interface ApiOptions {
  headers?: Record<string, string>;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: any;
  // Internal: marks a request that has already been retried after a refresh so
  // we never loop on 401s.
  retried?: boolean;
  // Internal: the token the refresh just returned. The replay sends it rather
  // than getToken(), which only sees the new token after React re-renders.
  token?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string; details?: string };
  timestamp: string;
}

/** GET/POST/PATCH /invoice-draft — see backend routes/invoice-drafts.ts. */
/** What the CURRENT estimate says about a local job (hours = travel + on-site): Finalize's pre-fill and fallback. */
export interface HourlyOpening {
  hours: number | null;
  travelHours: number | null;
  onsiteHours: number | null;
  minimumHours: number | null;
  crewSize: number | null;
  trucks: number | null;
  hasAdditionalTruckFee: boolean;
  /** The labour line's rate (crew preset or typed), else the tariff's; 0 = none set. Null off hourly. */
  hourlyRate: number | null;
}
export type DraftMode = 'ld' | 'hourly' | 'other';
/** A client line changed on the invoice for an admin to review against the contractor estimate (062). */
export interface ContractorNotice { key: string; text: string }
export interface InvoiceDraft {
  id: string;
  status: 'open' | 'issued' | 'discarded';
  invoiceDate: string;
  finalWeightLbs: number | null;
  /** The billed hours (local) — written by the server from the time fields. */
  finalHours: number | null;
  finalCrewSize: number | null;
  /** Local hourly (055). */
  finalTravelHours: number | null;
  finalOnsiteHours: number | null;
  finalMinimumHours: number | null;
  /** The rep's billable override; null = calculated. */
  finalBillableHours: number | null;
  finalTrucks: number | null;
  adjustments: { id: string; description: string; amount: number; taxable: boolean; sort?: number }[];
  /** Catalog fees added on the draft (055), with the client values copied when added. */
  fees: FinalDraftFee[];
  /** Estimate lines changed on the draft only (062): removed, or another qty / rate. */
  lineEdits: Record<string, FinalLineEdit>;
  tipMode: 'none' | 'pct' | 'amount';
  tipPct: number | null;
  tipAmount: number | null;
  notes: string | null;
  dvpReprice: boolean;
  issuedDocumentId: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface InvoiceDraftOpen {
  draft: InvoiceDraft;
  mode: DraftMode;
  estimate: HourlyOpening & { weightLbs: number | null };
  /** The server's price for the stored draft — what issuing would lock. */
  calc: any;
  /** Estimate lines the draft changed (062), quoted → billed (null = removed). */
  lineChanges: FinalLineChange[];
  /** Client lines changed on the invoice that the contractor estimate may need to follow (062). */
  contractorNotices: ContractorNotice[];
  /** The CURRENT estimate's inputs (FinalInputs + mode / dvp) for the local calcFinal preview. */
  inputs: any;
  dvpRates: Record<string, number | string | null>;
  dvp: { itemId: string; estimatePremium: number; finalPremium: number; declaredValue: number; applied: boolean } | null;
  warnings: string[];
  blocks: string[];
  canIssue: boolean;
  tipsEnabled: boolean;
  hasContractor: boolean;
  /** Collected so far, as the invoice counts it (paid includes card fees). */
  payments: { paidTotal: number; ccFeeTotal: number };
}
export interface InvoiceDraftNone {
  draft: null;
  mode: DraftMode;
  /** Finalize opens only from Booked; reopen-after-void is exempt. */
  stage: string | null;
  canFinalize: boolean;
  invoiced: boolean;
  /** `figures`: invoiceFigures — the portal's own numbers (locked lines, live paid / balance). */
  currentInvoice: { id: string; number: number; status: string; generated_at: string; recognition_date: string | null; figures: any } | null;
  lastIssued: { id: string; issued_document_id: string | null; issued_at: string } | null;
  opening: HourlyOpening & { weightLbs: number | null };
}
export type InvoiceDraftPayload = InvoiceDraftOpen | InvoiceDraftNone;

export interface LeadSearchParams {
  q?: string;
  stage?: string;
  move_type?: string;
  branch_id?: string;
  lead_source?: string;
  /** A user id, or '__unassigned__'. */
  rep?: string;
  unclaimed?: boolean;
  no_followup?: boolean;
  sort?: 'created' | 'job' | 'name' | 'move_date' | 'stage';
  dir?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
  with_followups?: boolean;
}
export interface RingCentralLeadLabel { id: string; first_name: string | null; last_name: string | null; job_no: number | string | null; stage: string | null }
export interface RingCentralCallLogged {
  call: any;
  created: boolean;
  lead: RingCentralLeadLabel | null;
  candidates: RingCentralLeadLabel[];
  outcomes: string[];
}
export interface LeadTextMessage {
  id: string; direction: 'Inbound' | 'Outbound'; body: string | null; status: string | null; at: string;
  sent_via: 'crm' | 'ringcentral_app' | 'inbound' | 'auto'; sender: string | null; lead_id: string | null; lead_job_no: number | string | null;
  /** An inbound text still unread in THIS viewer's Messages. */
  unread?: boolean;
}
export interface LeadTexts { phone: string | null; can_text: boolean; opted_out?: boolean; configured: boolean; max_length: number; messages: LeadTextMessage[] }
export interface LeadSearchResult { rows: any[]; total: number; page: number; page_size: number }
export interface LeadFacets { moveTypes: string[]; sources: string[]; total: number; unclaimed: number }
export interface DashboardSummary {
  total: number;
  unclaimed: number;
  stageCounts: Record<string, number>;
  pipelineValue: number;
  bookedRevenue: number;
  outstanding: number;
  /** The 8 newest leads; `value` is the estimate total, null when not quoted. */
  recent: any[];
}
export interface LeadLabel { id: string; job_no: number | null; job_number: string | null; first_name: string | null; last_name: string | null; stage: string | null }

export class UnauthorizedError extends Error {
  constructor() {
    super('UNAUTHORIZED');
    this.name = 'UnauthorizedError';
  }
}

class ApiClient {
  baseUrl: string;
  getToken: () => string | null;
  refreshToken?: () => Promise<string | null>;
  /** Sent as Idempotency-Key on writes from a client made by withIdempotencyKey(). */
  private idempotencyKey: string | null = null;

  constructor(
    baseUrl: string,
    getTokenFn: () => string | null,
    refreshTokenFn?: () => Promise<string | null>
  ) {
    this.baseUrl = baseUrl;
    this.getToken = getTokenFn;
    this.refreshToken = refreshTokenFn;
  }

  private async request<T>(endpoint: string, options: ApiOptions = {}): Promise<ApiResponse<T>> {
    const token = options.token ?? this.getToken();
    const url = `${this.baseUrl}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    // A repeat of the same intent (a double click that slipped through, a retry
    // after a dropped response) carries the same key; the server returns the
    // original result instead of acting twice (backend middleware/idempotency.ts).
    if (this.idempotencyKey && (options.method ?? 'GET') !== 'GET') {
      headers['Idempotency-Key'] = this.idempotencyKey;
    }

    const response = await fetch(url, {
      method: options.method || 'GET',
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Token expired or invalid. Try refreshing once, then replay the request
        // with the new token; if that's unavailable or still fails, surface an
        // UnauthorizedError so callers can log out.
        if (this.refreshToken && !options.retried) {
          const newToken = await this.refreshToken();
          if (newToken) {
            return this.request<T>(endpoint, { ...options, retried: true, token: newToken });
          }
        }
        throw new UnauthorizedError();
      }
      // Try to surface the backend's structured error message.
      let message = `API Error: ${response.status}`;
      let code: string | undefined;
      let details: unknown;
      try {
        const errBody = (await response.json()) as ApiResponse<unknown> & { error?: { details?: unknown } };
        if (errBody?.error?.message) message = errBody.error.message;
        code = errBody?.error?.code;
        // D1 Item 2: some errors carry a structured list — which blocks are
        // still empty, for instance. Dropping it here would leave the UI saying
        // "3 items need attention" with no way to say WHICH, which is the same
        // uninformative failure in a politer voice.
        details = (errBody?.error as any)?.details;
      } catch {
        // non-JSON error body — keep the generic message
      }
      // Attach code + status so callers can distinguish (e.g. a SUPPRESSED block
      // → offer "send anyway") without string-matching the message.
      const err = new Error(message) as Error & { code?: string; status?: number; details?: unknown };
      err.code = code;
      err.status = response.status;
      err.details = details;
      throw err;
    }

    return (await response.json()) as ApiResponse<T>;
  }

  /**
   * The same client, sending `key` as the Idempotency-Key on its writes.
   * Use with useAction: run((key) => api.withIdempotencyKey(key).addPayment(…)).
   */
  withIdempotencyKey(key: string): ApiClient {
    const c = new ApiClient(this.baseUrl, this.getToken, this.refreshToken);
    c.idempotencyKey = key;
    return c;
  }

  // Users
  getUsers() {
    return this.request<any[]>('/api/users');
  }
  getUser(id: string) {
    return this.request<any>(`/api/users/${id}`);
  }
  createUser(data: any) {
    return this.request<any>('/api/users', { method: 'POST', body: data });
  }
  updateUser(id: string, data: any) {
    return this.request<any>(`/api/users/${id}`, { method: 'PUT', body: data });
  }
  // Role / draw-permission update (Phase 8h.1; admin-only, guards self-demotion).
  patchUser(id: string, data: { role?: string; can_draw?: boolean }) {
    return this.request<any>(`/api/users/${id}`, { method: 'PATCH', body: data });
  }
  deleteUser(id: string) {
    return this.request<null>(`/api/users/${id}`, { method: 'DELETE' });
  }

  // Leads. There is no "every lead" call: each screen asks for what it shows.
  /**
   * The paged lead list (paging 2/4): All Clients, New Leads, the top-bar
   * search. Filters, search and sort run on the server; `total` is exact.
   */
  searchLeads(params: LeadSearchParams) {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === '' || v === false) continue;
      q.set(k, v === true ? '1' : String(v));
    }
    return this.request<LeadSearchResult>(`/api/leads/search?${q}`);
  }
  /** Every Dashboard figure, worked out on the server (paging 3/4). */
  getDashboardSummary() {
    return this.request<DashboardSummary>('/api/dashboard/summary');
  }
  /** The Dispatch Calendar's jobs for a date window (Booked, Invoiced, Closed). */
  getCalendarLeads(from: string, to: string) {
    return this.request<any[]>(`/api/leads/calendar?from=${from}&to=${to}`);
  }
  /** The list's filter options and the exact counts behind its tab badges. */
  getLeadFacets() {
    return this.request<LeadFacets>('/api/leads/facets');
  }
  /** Settings → Lead sources' display names — what a new lead's source is picked from. */
  getLeadSourceNames() {
    return this.request<string[]>('/api/leads/source-names');
  }
  /** Names and numbers for a known set of leads. */
  lookupLeads(ids: string[]) {
    return this.request<LeadLabel[]>('/api/leads/lookup', { method: 'POST', body: { ids } });
  }
  getLead(id: string) {
    return this.request<any>(`/api/leads/${id}`);
  }
  createLead(data: any) {
    return this.request<any>('/api/leads', { method: 'POST', body: data });
  }
  /**
   * `editSession` marks the first autosave of a notes editing session, so the
   * server logs it once. It is a QUERY parameter, not a body field: a backend
   * that predates it ignores the query and saves as before, where an unknown
   * body field would be written as a column and fail the save.
   */
  updateLead(id: string, data: any, opts: { editSession?: boolean } = {}) {
    return this.request<any>(`/api/leads/${id}${opts.editSession ? '?edit_session=1' : ''}`, { method: 'PUT', body: data });
  }
  // Claim an unassigned lead → assigns it to the current user (N1 "Grab").
  claimLead(id: string) {
    return this.request<any>(`/api/leads/${id}/claim`, { method: 'POST' });
  }
  /** One action on many leads (All Clients / New Leads). Skips come back with their reason. */
  bulkLeads(data: { action: 'grab' | 'assign' | 'stage'; lead_ids: string[]; assigned_rep_id?: string; stage?: string; reason?: string }) {
    return this.request<{ updated: { id: string; name: string }[]; skipped: { id: string; name: string; reason: string }[] }>(
      '/api/leads/bulk', { method: 'POST', body: data },
    );
  }
  deleteLead(id: string) {
    return this.request<null>(`/api/leads/${id}`, { method: 'DELETE' });
  }

  // Tariffs
  getTariffs() {
    return this.request<any[]>('/api/tariffs');
  }
  getTariff(id: string) {
    return this.request<any>(`/api/tariffs/${id}`);
  }
  createTariff(data: any) {
    return this.request<any>('/api/tariffs', { method: 'POST', body: data });
  }
  updateTariff(id: string, data: any) {
    return this.request<any>(`/api/tariffs/${id}`, { method: 'PUT', body: data });
  }
  deleteTariff(id: string) {
    return this.request<null>(`/api/tariffs/${id}`, { method: 'DELETE' });
  }

  // Branches
  getBranches() {
    return this.request<any[]>('/api/branches');
  }
  getBranch(id: string) {
    return this.request<any>(`/api/branches/${id}`);
  }
  createBranch(data: any) {
    return this.request<any>('/api/branches', { method: 'POST', body: data });
  }
  updateBranch(id: string, data: any) {
    return this.request<any>(`/api/branches/${id}`, { method: 'PUT', body: data });
  }
  deleteBranch(id: string) {
    return this.request<null>(`/api/branches/${id}`, { method: 'DELETE' });
  }

  // Fees
  getFees() {
    return this.request<any[]>('/api/fees');
  }
  getFee(id: string) {
    return this.request<any>(`/api/fees/${id}`);
  }
  createFee(data: any) {
    return this.request<any>('/api/fees', { method: 'POST', body: data });
  }
  updateFee(id: string, data: any) {
    return this.request<any>(`/api/fees/${id}`, { method: 'PUT', body: data });
  }
  deleteFee(id: string) {
    return this.request<null>(`/api/fees/${id}`, { method: 'DELETE' });
  }

  // Follow-ups. `assignee` (N4a): undefined = mine, 'all' = everyone, or a user id.
  // Badges never pass this, so badge counts stay personal.
  getFollowUps(assignee?: string) {
    return this.request<any[]>(`/api/follow-ups${assignee ? `?assignee=${encodeURIComponent(assignee)}` : ''}`);
  }
  // All follow-ups on a lead (every assignee) — shared per-lead view (N3 3a).
  getLeadFollowUps(leadId: string) {
    return this.request<any[]>(`/api/follow-ups?lead_id=${encodeURIComponent(leadId)}`);
  }
  // Mark a follow-up complete (N3 3b) — any company user; idempotent server-side.
  completeFollowUp(id: string) {
    return this.request<any>(`/api/follow-ups/${id}/complete`, { method: 'POST' });
  }
  getFollowUp(id: string) {
    return this.request<any>(`/api/follow-ups/${id}`);
  }
  createFollowUp(data: any) {
    return this.request<any>('/api/follow-ups', { method: 'POST', body: data });
  }
  updateFollowUp(id: string, data: any) {
    return this.request<any>(`/api/follow-ups/${id}`, { method: 'PUT', body: data });
  }
  // Toggle a follow-up's calendar pin (Phase 8h.2).
  setFollowUpPinned(id: string, pinned: boolean) {
    return this.request<any>(`/api/follow-ups/${id}`, { method: 'PATCH', body: { pinned } });
  }
  deleteFollowUp(id: string) {
    return this.request<null>(`/api/follow-ups/${id}`, { method: 'DELETE' });
  }

  // Personal reminders (N4b) — timetable-only notes, not tied to a lead. The GET
  // returns my own (private + shared) plus everyone else's shared, for one day.
  getReminders(date: string) {
    return this.request<any[]>(`/api/reminders?date=${encodeURIComponent(date)}`);
  }
  createReminder(data: any) {
    return this.request<any>('/api/reminders', { method: 'POST', body: data });
  }
  updateReminder(id: string, data: any) {
    return this.request<any>(`/api/reminders/${id}`, { method: 'PATCH', body: data });
  }
  deleteReminder(id: string) {
    return this.request<null>(`/api/reminders/${id}`, { method: 'DELETE' });
  }

  // Estimates
  getEstimates() {
    return this.request<any[]>('/api/estimates');
  }
  getEstimateById(id: string) {
    return this.request<any>(`/api/estimates/${id}`);
  }
  createEstimate(data: any) {
    return this.request<any>('/api/estimates', { method: 'POST', body: data });
  }
  updateEstimate(id: string, data: any) {
    return this.request<any>(`/api/estimates/${id}`, { method: 'PUT', body: data });
  }

  // Estimate line items + discounts (Phase 8c). The estimate for a lead is
  // created lazily on first fetch; the pure math lives in lib/estimate.ts.
  // A1 accessorial rate configuration (per tariff).
  getAccessorialRates(tariffId: string) {
    return this.request<any[]>(`/api/tariffs/${tariffId}/accessorials`);
  }
  updateAccessorialRate(id: string, data: any) {
    return this.request<any>(`/api/accessorials/${id}`, { method: 'PATCH', body: data });
  }
  getEstimate(leadId: string) {
    return this.request<{ estimate: any; items: any[] }>(`/api/leads/${leadId}/estimate`);
  }
  // `confirm` acknowledges a DUPLICATE_ACCESSORIAL warning and adds the line anyway.
  addEstimateItem(leadId: string, data: any, confirm = false) {
    return this.request<any>(`/api/leads/${leadId}/estimate/items${confirm ? '?confirm=1' : ''}`, { method: 'POST', body: data });
  }
  updateEstimateItem(id: string, data: any) {
    return this.request<any>(`/api/estimate-items/${id}`, { method: 'PATCH', body: data });
  }
  deleteEstimateItem(id: string) {
    return this.request<null>(`/api/estimate-items/${id}`, { method: 'DELETE' });
  }
  updateEstimateDiscounts(leadId: string, data: any) {
    return this.request<any>(`/api/leads/${leadId}/estimate/discounts`, { method: 'PATCH', body: data });
  }
  // 061: the office's coverage election. 409 VALUATION_LINE_EXISTS carries
  // details.items (and details.convertible) when a hand-made valuation line is
  // already on the estimate.
  setCoverage(leadId: string, data: {
    coverage: 'enhanced' | 'dvp';
    declared_value?: number;
    premium_override?: number | null;
    override_reason?: string | null;
    convert_item_id?: string | null;
  }) {
    return this.request<any>(`/api/leads/${leadId}/coverage`, { method: 'PUT', body: data });
  }

  // Company (returns the caller's own company; API scopes to it)
  getCompany() {
    return this.request<any[]>('/api/companies');
  }
  updateCompany(id: string, data: any) {
    return this.request<any>(`/api/companies/${id}`, { method: 'PUT', body: data });
  }

  // Config lists (option lists such as lead_source), company-scoped
  getConfigList(key: string) {
    return this.request<any[]>(`/api/config-lists?key=${encodeURIComponent(key)}`);
  }
  addConfigListItem(listKey: string, value: string) {
    return this.request<any>('/api/config-lists', { method: 'POST', body: { list_key: listKey, value } });
  }
  updateConfigListItem(id: string, value: string) {
    return this.request<any>(`/api/config-lists/${id}`, { method: 'PATCH', body: { value } });
  }
  deleteConfigListItem(id: string) {
    return this.request<null>(`/api/config-lists/${id}`, { method: 'DELETE' });
  }

  // Payments (Phase 8d)
  getPayments(leadId: string) {
    return this.request<any[]>(`/api/leads/${leadId}/payments`);
  }
  // E4 Item 4: `data.send_receipt` (default true server-side) emails a receipt;
  // override=1 bypasses the suppression block for the receipt.
  addPayment(leadId: string, data: any, override = false) {
    return this.request<any>(`/api/leads/${leadId}/payments${override ? '?override=1' : ''}`, { method: 'POST', body: data });
  }
  // Re-send the receipt for a SPECIFIC payment (Audit Report passes payment_id).
  resendPaymentReceipt(paymentId: string, override = false) {
    return this.request<{ ok: boolean }>(`/api/payments/${paymentId}/receipt${override ? '?override=1' : ''}`, { method: 'POST' });
  }
  updatePayment(id: string, data: any) {
    return this.request<any>(`/api/payments/${id}`, { method: 'PATCH', body: data });
  }
  deletePayment(id: string) {
    return this.request<null>(`/api/payments/${id}`, { method: 'DELETE' });
  }
  /** 048: Square's cost on a card payment. Entering a missing one is open to all; changing one is admin-only. */
  setPaymentSquareCost(id: string, squareCost: number) {
    return this.request<any>(`/api/payments/${id}/square-cost`, { method: 'PATCH', body: { square_cost: squareCost } });
  }

  // Documents (Phase 8d)
  getDocuments(leadId: string) {
    return this.request<any[]>(`/api/leads/${leadId}/documents`);
  }
  /** The document modal's preview: the filled, PDF-identical HTML (signatures, selections, completion certificate). */
  /** What to fix before a client signs the wrong thing: coverage mismatches and old live versions with client input. */
  getDocumentWarnings(leadId: string) {
    return this.request<{
      coverage: { id: string; number: string; label: string; template_id: string | null; out_for_signature: boolean; contract_shows: 'locked' | 'choice'; file_says: 'office_elected' | 'not_elected' }[];
      stale: { id: string; number: string; label: string; newer: { id: string; number: string } }[];
    }>(`/api/leads/${leadId}/document-warnings`);
  }
  getDocumentPreview(id: string) {
    return this.request<{ html: string }>(`/api/documents/${id}/preview`);
  }
  generateDocument(leadId: string, docType: string, recognitionDate?: string) {
    const body: any = { doc_type: docType };
    if (recognitionDate) body.recognition_date = recognitionDate; // invoices only (8g.3)
    return this.request<any>(`/api/leads/${leadId}/documents`, { method: 'POST', body });
  }
  // ---- Finalize (048): invoice drafts. Issuing is issueInvoiceFromDraft. ----
  getInvoiceDraft(leadId: string) {
    return this.request<InvoiceDraftPayload>(`/api/leads/${leadId}/invoice-draft`);
  }
  openInvoiceDraft(leadId: string, data: {
    invoice_date: string; final_weight_lbs?: number; final_crew_size?: number | null; hourly_rate?: number;
    final_travel_hours?: number; final_onsite_hours?: number; final_minimum_hours?: number | null;
    final_billable_hours?: number | null; final_trucks?: number | null;
  }) {
    return this.request<InvoiceDraftPayload>(`/api/leads/${leadId}/invoice-draft`, { method: 'POST', body: data });
  }
  updateInvoiceDraft(id: string, patch: Record<string, any>) {
    return this.request<InvoiceDraftPayload>(`/api/invoice-drafts/${id}`, { method: 'PATCH', body: patch });
  }
  discardInvoiceDraft(id: string) {
    return this.request<{ ok: true }>(`/api/invoice-drafts/${id}/discard`, { method: 'POST' });
  }
  /** The client invoice this draft would issue, in the portal's own payload shape (nothing saved). */
  previewInvoiceDraft(id: string) {
    return this.request<any>(`/api/invoice-drafts/${id}/preview`);
  }
  /** Admin: client lines changed on the invoice that the contractor estimate may need to follow (062). */
  getContractorNotices(leadId: string) {
    return this.request<{ draftId: string | null; notices: ContractorNotice[] }>(`/api/leads/${leadId}/invoice-draft/contractor-notices`);
  }
  /** Admin: the contractor pay was reviewed for these notices. */
  markContractorReviewed(draftId: string, keys: string[]) {
    return this.request<{ ok: true }>(`/api/invoice-drafts/${draftId}/contractor-reviewed`, { method: 'POST', body: { keys } });
  }
  reopenInvoiceDraft(id: string) {
    return this.request<InvoiceDraftPayload>(`/api/invoice-drafts/${id}/reopen`, { method: 'POST' });
  }
  /** The server re-prices from the stored draft and refuses (409 BLOCKED) while anything blocks it. */
  issueInvoiceFromDraft(leadId: string, draftId: string) {
    return this.request<any>(`/api/leads/${leadId}/documents`, { method: 'POST', body: { doc_type: 'invoice', draft_id: draftId } });
  }
  // D2: the server-generated PDF — the same file the client downloads.
  documentPdfUrl(id: string) {
    return `${this.baseUrl}/api/documents/${id}/pdf`;
  }
  async downloadDocumentPdf(id: string): Promise<{ blob: Blob; filename: string }> {
    const res = await fetch(this.documentPdfUrl(id), { headers: { Authorization: `Bearer ${this.getToken()}` } });
    if (!res.ok) {
      let msg = 'Could not generate the PDF.';
      try { msg = (await res.json())?.error?.message || msg; } catch { /* non-JSON */ }
      throw new Error(msg);
    }
    const cd = res.headers.get('Content-Disposition') || '';
    return { blob: await res.blob(), filename: cd.match(/filename="?([^";]+)"?/)?.[1] || 'document.pdf' };
  }
  createDocumentPortalLink(id: string) {
    return this.request<{ url: string; token: string }>(`/api/documents/${id}/portal-link`, { method: 'POST' });
  }
  updateDocumentStatus(id: string, action: 'sent' | 'paid' | 'void' | 'complete' | 'reopen') {
    return this.request<any>(`/api/documents/${id}`, { method: 'PATCH', body: { action } });
  }
  /**
   * D2 Item 3 — send several documents as ONE email carrying a link to each.
   * The response reports per document: `sent` and `failed` are disjoint, and
   * every `failed` entry says why. A caller that only checks `emailed` is
   * throwing away the part that matters.
   */
  sendDocumentBatch(leadId: string, documentIds: string[], override = false) {
    return this.request<{
      emailed: boolean;
      logId?: string | null;
      reason?: string;
      // True when at least one document in the send still needed signing: the
      // client got a "please sign" request, not a copy, and the rep is told so.
      signatureRequest?: boolean;
      isReminder?: boolean;
      sent: { id: string; label: string; number: string; needsSignature?: boolean }[];
      failed: { id: string; label: string; reason: string }[];
    }>(`/api/leads/${leadId}/documents/send-batch${override ? '?override=1' : ''}`, { method: 'POST', body: { document_ids: documentIds } });
  }
  // Email the customer their estimate (Phase E2). Sends only on this explicit call.
  // override=true bypasses the local suppression block ("send anyway").
  // What an estimate send would send, for the confirm dialog (read-only).
  checkEstimateSend(leadId: string) {
    return this.request<{ clientName: string; email: string; total: number; lineCount: number; warnings: string[]; blocked: string | null }>(`/api/leads/${leadId}/email-estimate/check`);
  }
  emailEstimate(leadId: string, override = false) {
    return this.request<{ ok: boolean; id: string }>(`/api/leads/${leadId}/email-estimate${override ? '?override=1' : ''}`, { method: 'POST' });
  }
  // Customer estimate portal (Phase P1) — staff: get/create the public link.
  createPortalLink(leadId: string) {
    return this.request<{ url: string; token: string; accepted_at: string | null }>(`/api/leads/${leadId}/portal-link`, { method: 'POST' });
  }

  // E3 Item 4 — outbound sends (all route through the one send layer server-side).
  // override=true bypasses the local suppression block ("send anyway").
  sendInvoice(docId: string, override = false) {
    return this.request<{ ok: boolean; id: string; logId: string | null }>(`/api/documents/${docId}/send${override ? '?override=1' : ''}`, { method: 'POST' });
  }
  sendInventoryRequest(leadId: string, override = false) {
    return this.request<{ ok: boolean; id: string; logId: string | null }>(`/api/leads/${leadId}/inventory-request/send${override ? '?override=1' : ''}`, { method: 'POST' });
  }
  sendContractorEstimate(leadId: string, override = false) {
    return this.request<{ ok: boolean; id: string; logId: string | null }>(`/api/leads/${leadId}/contractor-estimate/send${override ? '?override=1' : ''}`, { method: 'POST' });
  }
  sendContractorWorkOrder(leadId: string, override = false) {
    return this.request<{ ok: boolean; id: string; logId: string | null }>(`/api/leads/${leadId}/contractor-work-order/send${override ? '?override=1' : ''}`, { method: 'POST' });
  }
  // Book the lead (E3 Item 4c) — sets stage Booked, fires the in-app notification,
  // and optionally emails the confirmation to admins + the assigned rep.
  /**
   * `overrideReason`: an admin booking with no client payment recorded (the
   * server refuses everyone else — lib/bookingGate.ts). Logged to the activity log.
   * (`override` is the separate email-suppression "Send anyway".)
   */
  /**
   * `send` = the team email, `sendClient` = the client's booking confirmation.
   * `only` re-sends just that one email (no re-booking); `override` bypasses the
   * bounce check ("Send anyway").
   */
  bookLead(leadId: string, send: boolean, override = false, overrideReason?: string, opts: { sendClient?: boolean; only?: 'team' | 'client' } = {}) {
    const q = [override ? 'override=1' : '', opts.only ? `only=${opts.only}` : ''].filter(Boolean).join('&');
    return this.request<{
      lead: any; emailed: boolean; emailError: string | null; emailBlocked: boolean;
      clientEmailed: boolean; clientEmailError: string | null; clientEmailBlocked: boolean;
    }>(`/api/leads/${leadId}/book${q ? `?${q}` : ''}`, { method: 'POST', body: { send, send_client: !!opts.sendClient, ...(overrideReason ? { override_reason: overrideReason } : {}) } });
  }
  /** Settings → Email templates → Automatic emails. */
  getSystemEmails() {
    return this.request<{ emails: any[]; canEdit: boolean }>('/api/email-templates/system');
  }
  updateSystemEmail(key: string, data: { subject: string; body: string }) {
    return this.request<any>(`/api/email-templates/system/${key}`, { method: 'PUT', body: data });
  }
  // Send history for a lead (E3 Item 5b) — read from email_log, newest first.
  getEmailLog(leadId: string) {
    return this.request<any[]>(`/api/leads/${leadId}/email-log`);
  }

  // Live badge counts (N2) — polled ~every 3s by the top bar. `today` is the
  // caller's local date so follow-up "today" matches their calendar.
  getBadges(today: string) {
    return this.request<{
      newLeads: number;
      notificationsUnread: number;
      messages: number;
      followupsToday: number;
      followupsGeneralToday: number;
      followupsTimedToday: number;
      followupsOverdue: number;
    }>(`/api/badges?today=${encodeURIComponent(today)}`);
  }

  // Notifications (Phase P1) — in-app feed of customer actions.
  /** Newest first, `limit` of them; filters and the counts run on the server (paging 4/4). */
  getNotifications(opts: { limit?: number; unread?: boolean; type?: string } = {}) {
    const q = new URLSearchParams();
    if (opts.limit) q.set('limit', String(opts.limit));
    if (opts.unread) q.set('unread', '1');
    if (opts.type) q.set('type', opts.type);
    const qs = q.toString();
    return this.request<{ notifications: any[]; total: number; unreadCount: number; types: string[] }>(`/api/notifications${qs ? `?${qs}` : ''}`);
  }
  markNotificationRead(id: string) {
    return this.request<any>(`/api/notifications/${id}/read`, { method: 'PATCH' });
  }
  // Document templates (Phase 8i.1) — list (move-type filtered) + generate.
  /**
   * D1 — run the built-in template refresh DELIBERATELY and get back what it did
   * to each template. The same function runs lazily on getDocumentTemplates();
   * this is the version whose result you can see.
   */
  refreshBuiltInTemplates() {
    return this.request<{
      ok: boolean; error?: string; targetVersion: string;
      counts: { seeded: number; refreshed: number; alreadyCurrent: number; skipped: number; failed: number };
      rows: { name: string; moveType: string; outcome: string; reason: string; fromVersion?: string | null; toVersion?: string | null; chars?: number }[];
    }>('/api/document-templates/refresh-built-ins', { method: 'POST' });
  }
  getDocumentTemplates(moveType?: string) {
    const qs = moveType ? `?move_type=${encodeURIComponent(moveType)}` : '';
    return this.request<any[]>(`/api/document-templates${qs}`);
  }
  // Template editor (Phase 8i.3) — create / update / delete (admin, company-scoped).
  createDocumentTemplate(data: { name: string; move_type: string; content: string }) {
    return this.request<any>('/api/document-templates', { method: 'POST', body: data });
  }
  updateDocumentTemplate(id: string, data: { name?: string; move_type?: string; content?: string }) {
    return this.request<any>(`/api/document-templates/${id}`, { method: 'PATCH', body: data });
  }
  // D1 Item 7 — append-only template history.
  getTemplateVersions(id: string) {
    return this.request<any[]>(`/api/document-templates/${id}/versions`);
  }
  restoreTemplateVersion(id: string, version: number) {
    return this.request<any>(`/api/document-templates/${id}/restore`, { method: 'POST', body: { version } });
  }
  deleteDocumentTemplate(id: string) {
    return this.request<null>(`/api/document-templates/${id}`, { method: 'DELETE' });
  }
  generateTemplateDocument(leadId: string, templateId: string) {
    return this.request<any>(`/api/leads/${leadId}/documents`, { method: 'POST', body: { doc_type: 'template_document', template_id: templateId } });
  }
  // Template-document interactive inputs (Phase 8i.2): signatures + choiceboxes.
  /**
   * D1 Item 2 — initials. Its own endpoint because initials may be TYPED and a
   * signature may not, but it writes through the same recordMark() as a
   * signature, so the audit trail is identical by construction.
   */
  upsertInitials(id: string, data: { block_id: string; typed_value?: string; signature_data?: string; signed_name: string }) {
    return this.request<any>(`/api/documents/${id}/initials`, { method: 'PUT', body: data });
  }
  getDocumentInputs(id: string) {
    return this.request<{ signatures: any[]; choices: any[] }>(`/api/documents/${id}/inputs`);
  }
  upsertSignature(id: string, data: { block_id: string; signature_data: string; signed_name: string }) {
    return this.request<any>(`/api/documents/${id}/signatures`, { method: 'PUT', body: data });
  }
  upsertChoice(id: string, data: { block_id: string; selected_option: string }) {
    return this.request<any>(`/api/documents/${id}/choices`, { method: 'PUT', body: data });
  }
  // Edit an invoice's recognition date (8g.3) — re-buckets its revenue + paired
  // contractor cost into the new month.
  setInvoiceRecognitionDate(id: string, recognitionDate: string) {
    return this.request<any>(`/api/documents/${id}`, { method: 'PATCH', body: { action: 'set_recognition_date', recognition_date: recognitionDate } });
  }

  // Inventory / Cube Sheet (Phase 8e). Catalog is read from the DB (seeded via
  // migration 005); apply is the ONLY path that writes lead cuft/weight.
  getInventoryCatalog() {
    return this.request<{ catalog: any[]; rooms: any[] }>('/api/inventory/catalog');
  }
  getLeadInventory(leadId: string) {
    return this.request<{ header: any; items: any[] }>(`/api/leads/${leadId}/inventory`);
  }
  addInventoryItem(leadId: string, data: any) {
    return this.request<any>(`/api/leads/${leadId}/inventory/items`, { method: 'POST', body: data });
  }
  updateInventoryItem(id: string, data: any) {
    return this.request<any>(`/api/inventory-items/${id}`, { method: 'PATCH', body: data });
  }
  deleteInventoryItem(id: string) {
    return this.request<any>(`/api/inventory-items/${id}`, { method: 'DELETE' });
  }
  setInventoryMethod(leadId: string, data: any) {
    return this.request<any>(`/api/leads/${leadId}/inventory`, { method: 'PATCH', body: data });
  }
  /**
   * Lift the client-portal lock (P5a). Clearing submitted_at IS the reopen —
   * there is no second column, and the backend clears change_requested_at with
   * it because the request has been answered. Same endpoint as the method
   * picker; named separately because sending submitted_at is the ONE thing that
   * endpoint still accepts on it, and only ever as null.
   */
  // ---- Inventory search misses (045) ----
  /** Fire-and-forget: a rep search that found nothing. Always succeeds. */
  logInventorySearchMiss(data: { term: string; path: 'rep_cube' | 'rep_room'; kind?: 'search' | 'add_own'; lead_id?: string | null }) {
    return this.request<null>('/api/inventory/search-misses', { method: 'POST', body: data });
  }
  getInventorySearchMissesTop(days = 90) {
    return this.request<{ term: string; termNormalized: string; count: number; addedOwn: number; leads: number; lastAt: string; paths: string[] }[]>(`/api/inventory/search-misses/top?days=${days}`);
  }
  /** item_id: add the term to that item's search_terms. null: dismiss it. */
  resolveInventorySearchMiss(data: { term: string; term_normalized: string; item_id: string | null }) {
    return this.request<{ ok: true }>('/api/inventory/search-misses/resolve', { method: 'POST', body: data });
  }

  // ---- Inventory media (P5b) ----
  // The client's own uploads are posted from the portal page with fetch(), not
  // through this client: they are token-authorized, carry raw bytes, and need
  // upload progress. What lives here is the REP side.
  clearInventoryMedia(leadId: string, mediaId: string) {
    return this.request<{ ok: boolean }>(`/api/leads/${leadId}/inventory/media/${mediaId}`, { method: 'DELETE' });
  }

  getInventoryMediaUsage() {
    return this.request<{ files: number; bytes: number; gb: number; label: string; photos: any; videos: any }>('/api/inventory-media/usage');
  }

  getInventoryMediaTopLeads(limit = 20) {
    return this.request<any[]>(`/api/inventory-media/top-leads?limit=${limit}`);
  }

  /** Dry run by default. `apply` refuses server-side while the setting is off. */
  runInventoryMediaAutoClear(apply = false) {
    return this.request<any>(`/api/inventory-media/auto-clear${apply ? '?apply=1' : ''}`, { method: 'POST' });
  }

  // ---- Email on the lead feed (G1b) ----
  // Flat messages plus EVERY CRM send, linked or not. The caller joins the sends
  // to activity entries by email_log_id (migration 040): an entry naming a send
  // is enriched, a send no entry names becomes its own row. That is the rule
  // that keeps one email from appearing on the file twice.
  getLeadEmails(leadId: string) {
    return this.request<{
      messages: any[];
      sends: any[];
      counts: { messages: number; sends: number; unseen: number };
    }>(`/api/leads/${leadId}/emails`);
  }

  /**
   * Attachment bytes come from Gmail on demand — nothing is copied into our
   * storage. The failure messages are specific on purpose (mailbox unreachable,
   * message deleted, Gmail off in this environment), so they are surfaced rather
   * than replaced with a generic one.
   */
  async downloadEmailAttachment(id: string): Promise<{ blob: Blob; filename: string }> {
    const res = await fetch(`${this.baseUrl}/api/email-attachments/${id}/download`, {
      headers: { Authorization: `Bearer ${this.getToken()}` },
    });
    if (!res.ok) {
      let msg = 'Could not fetch that attachment.';
      try { msg = (await res.json())?.error?.message || msg; } catch { /* non-JSON */ }
      throw new Error(msg);
    }
    const cd = res.headers.get('Content-Disposition') || '';
    return { blob: await res.blob(), filename: cd.match(/filename="?([^";]+)"?/)?.[1] || 'attachment' };
  }

  getLeadEmailAddresses(leadId: string) {
    return this.request<any[]>(`/api/leads/${leadId}/lead-emails`);
  }
  /** Adding an address queues a 90-day backfill across every mailbox. */
  addLeadEmailAddress(leadId: string, email: string, label?: string) {
    return this.request<any>(`/api/leads/${leadId}/lead-emails`, { method: 'POST', body: { email, label } });
  }
  deleteLeadEmailAddress(id: string) {
    return this.request<{ ok: boolean }>(`/api/lead-emails/${id}`, { method: 'DELETE' });
  }

  // ---- Compose, reply and templates (G1c) ----

  /**
   * May this user send, and if not why. Asked BEFORE the compose panel opens so
   * the button can explain itself rather than failing on submit — the allowlist
   * case in particular, where a staging send would reach a real client.
   */
  getEmailCapability() {
    return this.request<{ canSend: boolean; reason: string; fromAddress: string; fromName: string }>(
      '/api/email/capability'
    );
  }

  /** Merge values for this lead and this sender. null means MISSING, not blank. */
  getMergeFields(leadId: string) {
    return this.request<Record<string, string | null>>(`/api/leads/${leadId}/merge-fields`);
  }

  /**
   * Send or reply as the logged-in user. multipart/form-data because of
   * attachments, so it bypasses `request` — which sets a JSON content type and
   * would make the browser send the boundary-less body Hono cannot parse.
   */
  async sendLeadEmail(leadId: string, p: {
    to: string[]; cc?: string[]; subject: string; bodyHtml: string;
    replyToMessageId?: string | null; attachments?: File[];
  }): Promise<any> {
    const form = new FormData();
    form.append('to', p.to.join(','));
    if (p.cc?.length) form.append('cc', p.cc.join(','));
    form.append('subject', p.subject);
    form.append('bodyHtml', p.bodyHtml);
    if (p.replyToMessageId) form.append('replyToMessageId', p.replyToMessageId);
    for (const f of p.attachments ?? []) form.append('attachments', f, f.name);

    const res = await fetch(`${this.baseUrl}/api/leads/${leadId}/emails/send`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${this.getToken()}` },
      body: form,
    });
    const json = await res.json().catch(() => null);
    if (!res.ok || !json?.success) {
      throw new Error(json?.error?.message || 'The email was not sent.');
    }
    return json.data;
  }

  // Text (SMS) templates (070): everyone reads; admins edit and reorder.
  getTextTemplates() {
    return this.request<{ templates: { id: string; name: string; body: string; sort_order: number }[]; canManage: boolean }>('/api/text-templates');
  }
  createTextTemplate(t: { name: string; body: string }) {
    return this.request<any>('/api/text-templates', { method: 'POST', body: t });
  }
  updateTextTemplate(id: string, t: { name?: string; body?: string }) {
    return this.request<any>(`/api/text-templates/${id}`, { method: 'PATCH', body: t });
  }
  deleteTextTemplate(id: string) {
    return this.request<any>(`/api/text-templates/${id}`, { method: 'DELETE' });
  }
  reorderTextTemplates(ids: string[]) {
    return this.request<any>('/api/text-templates/order', { method: 'PUT', body: { ids } });
  }
  /** The first-contact auto-text (071): the switch, each lead source's on/off and its template (072). */
  getAutoTextSettings() {
    return this.request<{ enabled: boolean; perSourceTemplates: boolean; sources: { id: string; name: string; active: boolean; auto_text: boolean; template_id: string | null }[]; canManage: boolean }>('/api/text-templates/auto-text');
  }
  setAutoTextEnabled(enabled: boolean) {
    return this.request<any>('/api/text-templates/auto-text', { method: 'PUT', body: { enabled } });
  }
  setSourceAutoText(sourceId: string, auto_text: boolean) {
    return this.request<any>(`/api/text-templates/auto-text/sources/${sourceId}`, { method: 'PUT', body: { auto_text } });
  }
  /** The text template a lead source's auto-text uses (072); null = First contact. */
  setSourceAutoTextTemplate(sourceId: string, template_id: string | null) {
    return this.request<any>(`/api/text-templates/auto-text/sources/${sourceId}`, { method: 'PUT', body: { template_id } });
  }

  getEmailTemplates() {
    return this.request<{ shared: any[]; mine: any[]; canManageShared: boolean }>('/api/email-templates');
  }
  createEmailTemplate(t: { name: string; subject: string; body: string; shared?: boolean }) {
    return this.request<any>('/api/email-templates', { method: 'POST', body: t });
  }
  updateEmailTemplate(id: string, t: { name?: string; subject?: string; body?: string }) {
    return this.request<any>(`/api/email-templates/${id}`, { method: 'PATCH', body: t });
  }
  deleteEmailTemplate(id: string) {
    return this.request<{ ok: boolean }>(`/api/email-templates/${id}`, { method: 'DELETE' });
  }

  // ---- Unread inbound email (G1d) ----
  // Read state is per message PER LEAD, so one action clears it in the feed and
  // in the top-bar dropdown at once — they are reading the same row.
  /** The newest `limit` unread client emails and the exact unread count. */
  getUnreadMessages(limit = 50) {
    return this.request<{ items: any[]; count: number }>(`/api/messages/unread?limit=${limit}`);
  }
  /**
   * Explicit, and ONE AT A TIME. Nothing marks a message read by being opened,
   * and there is deliberately no bulk equivalent — see the route's own note.
   */
  markMessageSeen(linkId: string) {
    return this.request<{ ok: boolean; changed: boolean }>(`/api/email-message-leads/${linkId}/seen`, { method: 'POST' });
  }

  /** The signature exactly as it will go out, plus which lines are omitted. */
  getSignaturePreview() {
    return this.request<{ html: string; omitted: Record<string, boolean> }>('/api/email/signature-preview');
  }

  // ---- Lead sources / intake (L1b) — ADMIN ONLY ----
  // The list carries a key PREVIEW only. The full secret has its own endpoint,
  // one source at a time, so it is never sitting in a payload the whole panel
  // holds — or in a browser cache, or in a screenshot of this page.
  /** Settings → Feedback (073). */
  getFeedbackSettings() {
    return this.request<{ enabled: boolean; sendEmail: boolean; sendText: boolean; googleUrl: string; bbbUrl: string; trustedprosUrl: string; canManage: boolean }>('/api/feedback/settings');
  }
  updateFeedbackSettings(patch: Partial<{ enabled: boolean; sendEmail: boolean; sendText: boolean; googleUrl: string; bbbUrl: string; trustedprosUrl: string }>) {
    return this.request<any>('/api/feedback/settings', { method: 'PUT', body: patch });
  }
  /** The lead file's Feedback line (073). */
  getLeadFeedback(leadId: string) {
    return this.request<{ request: any | null; canSendNow: boolean }>(`/api/leads/${leadId}/feedback`);
  }
  sendFeedbackNow(leadId: string) {
    return this.request<any>(`/api/leads/${leadId}/feedback/send-now`, { method: 'POST' });
  }
  /** Settings → Lead sources → Meta Lead Ads set-up (072). Admin only. */
  getMetaStatus() {
    return this.request<any>('/api/lead-sources/meta/status');
  }
  getLeadSources() {
    return this.request<{ sources: any[]; countsCappedAt: number; countsTruncated: boolean }>('/api/lead-sources');
  }
  revealLeadSourceKey(id: string) {
    return this.request<{ key: string }>(`/api/lead-sources/${id}/key`);
  }
  rotateLeadSourceKey(id: string) {
    return this.request<{ key: string; intakeUrl: string }>(`/api/lead-sources/${id}/rotate-key`, { method: 'POST' });
  }
  updateLeadSource(id: string, patch: { active?: boolean; displayName?: string; fieldMap?: Record<string, string> }) {
    return this.request<any>(`/api/lead-sources/${id}`, { method: 'PATCH', body: patch });
  }
  getLeadSourceLog(id: string, limit = 25) {
    return this.request<any[]>(`/api/lead-sources/${id}/log?limit=${limit}`);
  }
  /** Posts a realistic sample over HTTP to the real URL with the real key. */
  sendTestLead(id: string) {
    return this.request<{ url: string; status: string; leadId: string | null; jobNo: string | null }>(
      `/api/lead-sources/${id}/test`, { method: 'POST' }
    );
  }
  getIntakeLogRow(id: string) {
    return this.request<any>(`/api/intake-log/${id}`);
  }
  /** Re-runs the stored body through the mapper as it stands now. */
  reprocessIntakeLog(id: string) {
    return this.request<{ status: string; leadId?: string; jobNo?: string }>(
      `/api/intake-log/${id}/reprocess`, { method: 'POST' }
    );
  }

  // ---- Gmail sync (G1a) — ADMIN ONLY ----
  // The status payload deliberately reports the ENVIRONMENT as well as the
  // mailboxes: staging and production share one Google Workspace and only one of
  // them polls it, so "nothing here" is far more often the wrong environment than
  // a broken integration. No part of the service account key is ever returned —
  // `serviceAccountConfigured` is a boolean.
  /** Settings → RingCentral (admin): sync mode, per-extension state, subscription. */
  getRingCentralStatus() {
    return this.request<any>('/api/ringcentral/status');
  }
  resubscribeRingCentral() {
    return this.request<any>('/api/ringcentral/subscription', { method: 'POST' });
  }
  syncRingCentralNow() {
    return this.request<any>('/api/ringcentral/sync', { method: 'POST' });
  }
  getGmailSyncStatus() {
    return this.request<{
      environment: {
        nodeEnv: string; syncEnabled: boolean; sendEnabled: boolean; allowlist: string[];
        serviceAccountConfigured: boolean; serviceAccountError: string | null;
        configError: string | null; pollerRunning: boolean; syncing: boolean;
        initialSyncDays: number; domain: string;
      };
      totals: { messages: number; unseen: number };
      mailboxes: any[];
      backfill: { pending: number; running: number; errored: number; recent: any[] };
    }>('/api/gmail/sync-status');
  }

  /** Runs a pass now. Omit userId for every mailbox; 409 when sync is switched off. */
  resyncGmail(userId?: string) {
    return this.request<any>('/api/gmail/resync', { method: 'POST', body: userId ? { userId } : {} });
  }

  setGmailMailboxEnabled(userId: string, enabled: boolean) {
    return this.request<any>(`/api/gmail/mailboxes/${userId}`, { method: 'PATCH', body: { enabled } });
  }

  reopenClientInventory(leadId: string) {
    return this.request<any>(`/api/leads/${leadId}/inventory`, { method: 'PATCH', body: { submitted_at: null } });
  }
  applyInventoryTotals(leadId: string) {
    return this.request<any>(`/api/leads/${leadId}/inventory/apply`, { method: 'POST' });
  }

  // Contractors + contractor estimate / dispatch (Phase 8f). B2B: no tax, no
  // discounts; broker enters amounts. Privacy: contractor-facing surfaces never
  // include customer totals/payments/margin.
  getContractors() {
    return this.request<any[]>('/api/contractors');
  }
  updateContractor(id: string, data: any) {
    return this.request<any>(`/api/contractors/${id}`, { method: 'PATCH', body: data });
  }
  deleteContractor(id: string) {
    return this.request<null>(`/api/contractors/${id}`, { method: 'DELETE' });
  }
  createContractor(data: any) {
    return this.request<any>('/api/contractors', { method: 'POST', body: data });
  }
  getContractorEstimate(leadId: string) {
    return this.request<{ assignment: any; estimate: any; items: any[] }>(`/api/leads/${leadId}/contractor-estimate`);
  }
  assignContractor(leadId: string, contractorId: string | null) {
    return this.request<any>(`/api/leads/${leadId}/assign-contractor`, { method: 'POST', body: { contractor_id: contractorId } });
  }
  generateContractorEstimate(leadId: string) {
    return this.request<any>(`/api/leads/${leadId}/contractor-estimate`, { method: 'POST' });
  }
  updateContractorEstimateItem(id: string, data: any) {
    return this.request<any>(`/api/contractor-estimate-items/${id}`, { method: 'PATCH', body: data });
  }
  addContractorEstimateItem(data: any, confirm = false) {
    return this.request<any>(`/api/contractor-estimate-items${confirm ? '?confirm=1' : ''}`, { method: 'POST', body: data });
  }
  deleteContractorEstimateItem(id: string) {
    return this.request<any>(`/api/contractor-estimate-items/${id}`, { method: 'DELETE' });
  }
  /** §9.3: local hourly, not approved — labour at the tariff %, fees at their contractor share. */
  applyContractorLocalDefaults(leadId: string) {
    return this.request<any>(`/api/leads/${leadId}/contractor-estimate/apply-local-defaults`, { method: 'POST' });
  }
  updateContractorEstimateStatus(leadId: string, action: string, reason?: string) {
    return this.request<any>(`/api/leads/${leadId}/contractor-estimate`, { method: 'PATCH', body: { action, reason } });
  }
  generateContractorWorkOrder(leadId: string) {
    return this.request<any>(`/api/leads/${leadId}/contractor-work-order`, { method: 'POST' });
  }
  /** Activity line "Driver text copied" (the text itself is built and copied in the browser). */
  logDriverTextCopied(leadId: string) {
    return this.request<null>(`/api/leads/${leadId}/driver-text-copied`, { method: 'POST' });
  }

  // Accounting (Phase 8g.1) — ADMIN ONLY. Balances are derived server-side.
  getAccountingBalances() {
    return this.request<any>('/api/accounting/balances');
  }
  /** Activity by ACCOUNTING date for a period: { label, from, to, total, rows }. */
  getAccountingActivity(period: 'month' | 'ytd' | 'all' = 'month', limit = 8, offset = 0) {
    return this.request<any>(`/api/accounting/activity?period=${period}&limit=${limit}&offset=${offset}`);
  }
  /** The Overview's period cards: revenue, profit, expenses, draws. */
  getAccountingPeriod(period: 'month' | 'ytd' | 'all' = 'month') {
    return this.request<any>(`/api/accounting/period?period=${period}`);
  }
  // Month-end statements (Accounting → Statements; migration 064 for close / reopen).
  getStatements() {
    return this.request<{ months: any[]; migrationMissing: boolean }>('/api/accounting/statements');
  }
  getStatement(month: string) {
    return this.request<{ statement: any; closed: { by: string; byName: string; at: string } | null; changes: { label: string; closed: number; live: number; delta: number }[]; migrationMissing: boolean }>(`/api/accounting/statements/${month}`);
  }
  closeMonth(month: string) {
    return this.request<any>(`/api/accounting/statements/${month}/close`, { method: 'POST' });
  }
  reopenMonth(month: string) {
    return this.request<null>(`/api/accounting/statements/${month}/close`, { method: 'DELETE' });
  }
  async downloadStatementXlsx(month: string): Promise<{ blob: Blob; filename: string }> {
    const res = await fetch(`${this.baseUrl}/api/accounting/statements/${month}/xlsx`, { headers: { Authorization: `Bearer ${this.getToken()}` } });
    if (!res.ok) {
      let msg = 'Could not build the Excel file.';
      try { msg = (await res.json())?.error?.message || msg; } catch { /* non-JSON */ }
      throw new Error(msg);
    }
    const cd = res.headers.get('Content-Disposition') || '';
    return { blob: await res.blob(), filename: cd.match(/filename="?([^";]+)"?/)?.[1] || `Statement ${month}.xlsx` };
  }
  /** The month's job review (every job invoiced in it, with Finalize's margin); the stored one when closed. */
  getJobReview(month: string) {
    return this.request<{ review: any; closed: { by: string; byName: string; at: string } | null; closedWithout: boolean; changes: { label: string; closed: number; live: number; delta: number }[] }>(`/api/accounting/statements/${month}/jobs`);
  }
  async downloadJobReviewXlsx(month: string): Promise<{ blob: Blob; filename: string }> {
    const res = await fetch(`${this.baseUrl}/api/accounting/statements/${month}/jobs/xlsx`, { headers: { Authorization: `Bearer ${this.getToken()}` } });
    if (!res.ok) {
      let msg = 'Could not build the Excel file.';
      try { msg = (await res.json())?.error?.message || msg; } catch { /* non-JSON */ }
      throw new Error(msg);
    }
    const cd = res.headers.get('Content-Disposition') || '';
    return { blob: await res.blob(), filename: cd.match(/filename="?([^";]+)"?/)?.[1] || `Job review ${month}.xlsx` };
  }
  getAccountingMonthly() {
    return this.request<any[]>('/api/accounting/monthly');
  }
  getExpenseCategories() {
    return this.request<any[]>('/api/accounting/expense-categories');
  }
  getExpenses() {
    return this.request<any[]>('/api/expenses');
  }
  addExpense(data: any) {
    return this.request<any>('/api/expenses', { method: 'POST', body: data });
  }
  updateExpense(id: string, data: any) {
    return this.request<any>(`/api/expenses/${id}`, { method: 'PATCH', body: data });
  }
  deleteExpense(id: string) {
    return this.request<null>(`/api/expenses/${id}`, { method: 'DELETE' });
  }
  getDraws() {
    return this.request<any[]>('/api/draws');
  }
  addDraw(data: any) {
    return this.request<any>('/api/draws', { method: 'POST', body: data });
  }
  updateDraw(id: string, data: any) {
    return this.request<any>(`/api/draws/${id}`, { method: 'PATCH', body: data });
  }
  deleteDraw(id: string) {
    return this.request<null>(`/api/draws/${id}`, { method: 'DELETE' });
  }
  getAdjustments() {
    return this.request<any[]>('/api/adjustments');
  }
  addAdjustment(data: any) {
    return this.request<any>('/api/adjustments', { method: 'POST', body: data });
  }
  updateAdjustment(id: string, data: any) {
    return this.request<any>(`/api/adjustments/${id}`, { method: 'PATCH', body: data });
  }
  deleteAdjustment(id: string) {
    return this.request<null>(`/api/adjustments/${id}`, { method: 'DELETE' });
  }
  /** Every contractor payment, or one job's (`leadId`) — the Dispatch tab. */
  getContractorPayments(leadId?: string) {
    return this.request<any[]>(`/api/contractor-payments${leadId ? `?lead_id=${encodeURIComponent(leadId)}` : ''}`);
  }
  addContractorPayment(leadId: string, data: any) {
    return this.request<any>(`/api/leads/${leadId}/contractor-payments`, { method: 'POST', body: data });
  }
  /** What we owe each contractor, with the reconciliation to the balance equation. */
  /** The effective-dated partner split schedule, oldest first. */
  getPartnerSplits() {
    return this.request<any[]>('/api/partner-splits');
  }
  /** Replaces the whole schedule — see the route's note on why it is not a patch. */
  savePartnerSplits(splits: { effectiveFrom: string; adrianPct: number; darylPct: number; note?: string | null }[]) {
    return this.request<any[]>('/api/partner-splits', { method: 'PUT', body: { splits } });
  }
  // ---- Finalize phase 2 (049): the tab's admin-only sections ----
  getFinalizeAdmin(leadId: string) {
    return this.request<{ contractor: any; commission: any; margin: any }>(`/api/finalize-admin/leads/${leadId}`);
  }
  /** Commission (step 3: setup + preview only — nothing is recorded). */
  setCommissionOverride(leadId: string, body: { mode: 'pct' | 'flat'; pct?: number; amount?: number; note?: string }) {
    return this.request<{ commission: any }>(`/api/finalize-admin/leads/${leadId}/commission-override`, { method: 'PUT', body });
  }
  clearCommissionOverride(leadId: string) {
    return this.request<{ commission: any }>(`/api/finalize-admin/leads/${leadId}/commission-override`, { method: 'DELETE' });
  }
  getCommissionRates() {
    return this.request<{ userId: string; name: string; role: string; pct: number; set: boolean }[]>('/api/finalize-admin/commission-rates');
  }
  setCommissionRate(userId: string, pct: number) {
    return this.request<{ userId: string; pct: number }>(`/api/finalize-admin/commission-rates/${userId}`, { method: 'PUT', body: { pct } });
  }
  acceptContractorReprice(id: string) {
    return this.request<{ contractor: any }>(`/api/finalize-admin/reprices/${id}/accept`, { method: 'POST' });
  }
  keepContractorReprice(id: string) {
    return this.request<{ contractor: any }>(`/api/finalize-admin/reprices/${id}/keep`, { method: 'POST' });
  }
  /** §8.2: contractor lines edited on Finalize (admin). An approved estimate's edit is recorded and audited. */
  addFinalizeContractorItem(ceId: string, data: { fee_id?: string; description?: string; amount?: number }, confirm = false) {
    return this.request<{ contractor: any }>(`/api/finalize-admin/contractor-estimates/${ceId}/items${confirm ? '?confirm=1' : ''}`, { method: 'POST', body: data });
  }
  updateFinalizeContractorItem(id: string, data: Record<string, any>) {
    return this.request<{ contractor: any }>(`/api/finalize-admin/contractor-items/${id}`, { method: 'PATCH', body: data });
  }
  deleteFinalizeContractorItem(id: string) {
    return this.request<{ contractor: any }>(`/api/finalize-admin/contractor-items/${id}`, { method: 'DELETE' });
  }
  /** 048: card payments with no Square cost (admin, Accounting). */
  getSquareCostNeeded() {
    return this.request<any[]>('/api/accounting/square-cost-needed');
  }
  getContractorLedger() {
    return this.request<any>('/api/contractor-ledger');
  }
  /**
   * Pay a contractor directly. `allocations` writes one row per job sharing a
   * reference; omit it for a payment on account that names no job yet.
   */
  payContractor(data: {
    contractor_id: string; amount: number; paid_on?: string; method?: string | null;
    notes?: string | null; reference?: string | null;
    allocations?: { lead_id: string; contractor_estimate_id?: string | null; amount: number }[];
  }) {
    return this.request<any[]>('/api/contractor-payments', { method: 'POST', body: data });
  }
  deleteContractorPayment(id: string) {
    return this.request<null>(`/api/contractor-payments/${id}`, { method: 'DELETE' });
  }
  // ---- Sales commission (049, admin) ----
  getCommissionLedger() {
    return this.request<any>('/api/commission-ledger');
  }
  getCommissionPayments() {
    return this.request<any[]>('/api/commission-payments');
  }
  payCommission(data: { rep_user_id: string; amount: number; paid_on?: string; method?: string | null; notes?: string | null; commission_id?: string | null }) {
    return this.request<any>('/api/commission-payments', { method: 'POST', body: data });
  }
  deleteCommissionPayment(id: string) {
    return this.request<null>(`/api/commission-payments/${id}`, { method: 'DELETE' });
  }

  // Activity log
  // Per-rep sales-contact stats (N4c). Caller passes the three period boundaries
  // as instants (computed in the viewer's tz); server returns only the 9 numbers.
  // rep: 'all' | userId. 403 server-side if a rep requests anyone but themselves.
  getActivityStats(p: { rep: string; todayStart: string; weekStart: string; monthStart: string }) {
    const q = new URLSearchParams({ rep: p.rep, todayStart: p.todayStart, weekStart: p.weekStart, monthStart: p.monthStart });
    return this.request<{ today: any; week: any; month: any }>(`/api/activity-stats?${q.toString()}`);
  }
  /** category: 'sales' = the lead's right-hand log; 'dispatch' = the Dispatch History popup (admins). */
  getActivityLog(leadId?: string, category?: 'sales' | 'dispatch') {
    const q = new URLSearchParams();
    if (leadId) q.set('lead_id', leadId);
    if (category) q.set('category', category);
    const qs = q.toString() ? `?${q}` : '';
    return this.request<any[]>(`/api/activity-log${qs}`);
  }
  // RingCentral call logging (plan step 3). POST is idempotent on the call's
  // telephony_session_id; PATCH saves the "Notes about your call" modal.
  logRingCentralCall(data: any) {
    return this.request<RingCentralCallLogged>('/api/ringcentral/calls', { method: 'POST', body: data });
  }
  /** A lead's Texts thread: the client's whole history by phone number (RingCentral step 5). */
  getLeadTexts(leadId: string) {
    return this.request<LeadTexts>(`/api/leads/${leadId}/texts`);
  }
  /** Send from the main number. Use withIdempotencyKey: one press, one text. */
  sendLeadText(leadId: string, text: string) {
    return this.request<any>(`/api/leads/${leadId}/texts`, { method: 'POST', body: { text } });
  }
  /** Mark ONE inbound text / missed call read (Messages; shared like an email's read mark). */
  markRingCentralSeen(kind: 'text' | 'call', id: string) {
    return this.request<{ ok: boolean; changed: boolean }>(`/api/ringcentral/${kind === 'text' ? 'texts' : 'calls'}/${id}/seen`, { method: 'POST' });
  }
  /** One text and the leads with its number (the unmatched lookup). */
  getRingCentralText(id: string) {
    return this.request<{ text: any; candidates: RingCentralLeadLabel[] }>(`/api/ringcentral/texts/${id}`);
  }
  /** Unmatched dialog (069): a new file from this call / text, with every unmatched item from its number. */
  createLeadFromRingCentral(kind: 'text' | 'call', id: string, name: string) {
    return this.request<{ lead_id: string; moved: { texts: number; calls: number } }>(`/api/ringcentral/${kind === 'text' ? 'texts' : 'calls'}/${id}/create-lead`, { method: 'POST', body: { name } });
  }
  /** Unmatched dialog (069): spam / not relevant; optionally ignore the number from now on. */
  dismissRingCentralItem(kind: 'text' | 'call', id: string, ignoreNumber: boolean) {
    return this.request<{ dismissed: boolean; ignored: boolean }>(`/api/ringcentral/${kind === 'text' ? 'texts' : 'calls'}/${id}/dismiss`, { method: 'POST', body: { ignore_number: ignoreNumber } });
  }
  /** Step 9 (admin): the account's user extensions and the CRM users' mapping. */
  getRingCentralExtensions() {
    return this.request<{ extensions: { id: string; number: string; name: string; status: string | null }[]; users: { id: string; name: string; role: string; extension_id: string | null }[] }>('/api/ringcentral/extensions');
  }
  setRingCentralUserMap(userId: string, extensionId: string | null) {
    return this.request<{ user_id: string; extension_id: string | null }>('/api/ringcentral/user-map', { method: 'PUT', body: { user_id: userId, extension_id: extensionId } });
  }
  getRingCentralIgnored() {
    return this.request<{ id: string; number: string; created_at: string; created_by: string | null }[]>('/api/ringcentral/ignored');
  }
  unignoreRingCentralNumber(id: string) {
    return this.request<{ ok: boolean }>(`/api/ringcentral/ignored/${id}`, { method: 'DELETE' });
  }
  linkRingCentralText(id: string, leadId: string) {
    return this.request<{ id: string; lead_id: string }>(`/api/ringcentral/texts/${id}/link`, { method: 'POST', body: { lead_id: leadId } });
  }
  getRingCentralCall(id: string) {
    return this.request<RingCentralCallLogged>(`/api/ringcentral/calls/${id}`);
  }
  saveRingCentralCallNotes(id: string, data: {
    outcome?: string | null; notes?: string | null; lead_id?: string;
    follow_up?: { date: string; time?: string | null; description?: string; assigned_to_user_id?: string } | null;
  }) {
    return this.request<RingCentralCallLogged>(`/api/ringcentral/calls/${id}`, { method: 'PATCH', body: data });
  }
  createActivityLog(data: any) {
    return this.request<any>('/api/activity-log', { method: 'POST', body: data });
  }
  // Pin / unpin a note (Phase 8h.2) — sets pinned + pinned_at server-side.
  setActivityPinned(id: string, pinned: boolean) {
    return this.request<any>(`/api/activity-log/${id}`, { method: 'PATCH', body: { pinned } });
  }
}

export default ApiClient;
